<template>
  <div class="internal-employee-form-panel">
    <!-- Body -->
    <div class="form-body p-3">
      <b-form @submit.prevent="handleSubmit">
        <div class="section-card mb-4">
          <h6 class="section-title">Informações do Funcionário</h6>

          <b-row>
            <b-col md="6">
              <b-form-group label="Nome Completo" label-for="employee-name">
                <b-form-input
                  id="employee-name"
                  v-model="form.name"
                  required
                  placeholder="Digite o nome completo"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Email" label-for="employee-email">
                <b-form-input
                  id="employee-email"
                  v-model="form.email"
                  type="email"
                  placeholder="Digite o email (opcional)"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label="Telefone" label-for="employee-phone">
                <b-form-input
                  id="employee-phone"
                  v-model="form.phone"
                  placeholder="Digite o telefone (opcional)"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="CPF/CNPJ" label-for="employee-cpfcnpj">
                <b-form-input
                  id="employee-cpfcnpj"
                  v-model="form.cpfcnpj"
                  placeholder="Digite o CPF ou CNPJ (opcional)"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label="Setor" label-for="employee-area">
                <b-form-select
                  id="employee-area"
                  v-model="form.area"
                  :options="sectorSelectOptions"
                  :disabled="!hasSectors"
                ></b-form-select>
                <small v-if="!hasSectors" class="text-danger">
                  Cadastre setores em Parâmetros Gerais para continuar.
                </small>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Cargo" label-for="employee-position">
                <b-form-input
                  id="employee-position"
                  v-model="form.position"
                  placeholder="Digite o cargo (opcional)"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label="Status" label-for="employee-active">
                <b-form-checkbox
                  id="employee-active"
                  v-model="form.active"
                  switch
                >
                  Ativo
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <!-- Botões de ação -->
        <div class="form-actions d-flex justify-content-between">
          <div>
            <b-button
              v-if="isEditing"
              variant="danger"
              :disabled="submitting"
              @click="handleDelete"
            >
              <i class="fas fa-trash"></i> Excluir
            </b-button>
          </div>
          <div>
            <b-button
              type="button"
              variant="secondary"
              :disabled="submitting"
              class="mr-2"
              @click="$emit('close')"
            >
              Cancelar
            </b-button>
            <b-button type="submit" variant="primary" :disabled="submitting">
              <b-spinner v-if="submitting" small></b-spinner>
              <i v-else class="fas fa-save"></i>
              {{ isEditing ? "Atualizar" : "Salvar" }}
            </b-button>
          </div>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
export default {
  name: "InternalEmployeeForm",
  props: {
    employee: {
      type: Object,
      default: null,
    },
    sectorOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      submitting: false,
      form: {
        name: "",
        email: "",
        phone: "",
        area: "",
        position: "",
        cpfcnpj: "",
        active: true,
      },
    };
  },
  computed: {
    isEditing() {
      return !!this.employee;
    },
    hasSectors() {
      return this.sectorOptions.length > 0;
    },
    sectorSelectOptions() {
      const options = [
        {
          value: "",
          text: this.hasSectors
            ? "Por favor selecione um setor"
            : "Nenhum setor cadastrado",
          disabled: true,
        },
      ];

      for (const sector of this.sectorOptions) {
        options.push({ value: sector, text: sector });
      }

      return options;
    },
  },
  watch: {
    employee: {
      handler(newEmployee) {
        if (newEmployee) {
          this.form = {
            id: newEmployee._id,
            name: newEmployee.name,
            email: newEmployee.email,
            phone: newEmployee.phone || "",
            area: this.normalizeArea(newEmployee.area),
            position: newEmployee.position || "",
            cpfcnpj: newEmployee.cpfcnpj || "",
            active: newEmployee.active,
          };
        } else {
          this.resetForm();
        }
      },
      immediate: true,
    },
    sectorOptions: {
      handler() {
        this.form.area = this.normalizeArea(this.form.area);
      },
      deep: true,
    },
  },
  methods: {
    normalizeArea(area) {
      const value = String(area || "").trim();
      if (!value || !this.hasSectors) {
        return "";
      }
      return this.sectorOptions.includes(value) ? value : "";
    },
    resetForm() {
      this.form = {
        name: "",
        email: "",
        phone: "",
        area: "",
        position: "",
        cpfcnpj: "",
        active: true,
      };
    },
    async handleSubmit() {
      this.submitting = true;
      try {
        // Validação básica
        if (!this.form.name.trim()) {
          throw new Error("Nome é obrigatório");
        }

        if (!this.hasSectors) {
          throw new Error(
            "Nenhum setor cadastrado em Parâmetros Gerais. Cadastre setores para continuar."
          );
        }

        if (!this.form.area) {
          throw new Error("Setor é obrigatório");
        }

        if (!this.sectorOptions.includes(this.form.area)) {
          throw new Error(
            "Setor inválido. Selecione um setor cadastrado em Parâmetros Gerais."
          );
        }

        // Validação de email (se preenchido)
        if (this.form.email && this.form.email.trim()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(this.form.email)) {
            throw new Error("Email inválido");
          }
        }

        this.$emit("submit", { ...this.form });
      } catch (error) {
        this.$bvToast.toast(error.message, {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.submitting = false;
      }
    },
    async handleDelete() {
      if (!this.employee) return;

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Tem certeza que deseja excluir o funcionário "${this.employee.name}"?`,
        {
          title: "Confirmar exclusão",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Sim, excluir",
          cancelTitle: "Cancelar",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        }
      );

      if (confirmed) {
        this.$emit("delete", this.employee);
      }
    },
  },
};
</script>

<style scoped>
.internal-employee-form-panel {
  background: white;
  border-radius: 8px;
}

.form-body {
  max-height: 90vh;
  overflow-y: auto;
}

.section-card {
  border-radius: 8px;
  padding: 1.5rem;
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.form-actions {
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

/* Estilização dos campos */
.form-group label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #ced4da;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus,
.form-select:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Estilização do checkbox switch */
.custom-switch .custom-control-label::before {
  border-radius: 0.75rem;
}

.custom-switch .custom-control-label::after {
  border-radius: 0.75rem;
}

/* Responsividade */
@media (max-width: 768px) {
  .form-actions {
    flex-direction: column;
    gap: 1rem;
  }

  .form-actions > div {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
  }
}
</style>
